import {
  Button,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { AllowlistEntry, listAllowlistEntries } from '../../../services/financeTransferService';

export interface TransferFormState {
  asset: '' | 'SOL' | 'USDC';
  toAddress: string;
  amount: string;
  reason: string;
  isValid: boolean;
}

interface SignTransactionFormProps {
  chain: string;
  address: string;
  nativeBalance: string;
  usdcBalance: string;
  onChange: (form: TransferFormState) => void;
}

const AMOUNT_INPUT = /^[0-9]*\.?[0-9]{0,6}$/;
const AMOUNT_VALID = /^\d+(\.\d{1,6})?$/;

const TransferRequestForm = ({
  chain,
  address,
  nativeBalance,
  usdcBalance,
  onChange,
}: SignTransactionFormProps) => {
  const translate = useTranslate();

  const choices = [
    { id: 'SOL' as const, name: `${nativeBalance} (SOL)`, balance: Number(nativeBalance) },
    { id: 'USDC' as const, name: `${usdcBalance} (USDC)`, balance: Number(usdcBalance) },
  ];

  const [asset, setAsset] = useState<'' | 'SOL' | 'USDC'>('');
  const [amount, setAmount] = useState<string>('');
  const [toAddress, setToAddress] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [allowed, setAllowed] = useState<AllowlistEntry[]>([]);
  const [allowedError, setAllowedError] = useState<string>('');

  // The destination is picked from the active allowlist; the server checks it again at request and approval.
  useEffect(() => {
    listAllowlistEntries({ size: 100, active: true })
      .then((res) => setAllowed((res.data ?? []).filter((e) => e.chain === chain)))
      .catch((e) => setAllowedError(e?.message || 'Error'));
  }, [chain]);

  const currentChoice = choices.find((c) => c.id === asset);
  const exceedsBalance = !!currentChoice && Number(amount) > currentChoice.balance;

  const isValid = useMemo(
    () =>
      !!currentChoice &&
      AMOUNT_VALID.test(amount) &&
      /[1-9]/.test(amount) &&
      !exceedsBalance &&
      toAddress.length > 0,
    [currentChoice, amount, exceedsBalance, toAddress]
  );

  useEffect(() => {
    onChange({ asset, toAddress, amount, reason, isValid });
  }, [asset, toAddress, amount, reason, isValid, onChange]);

  return (
    <Stack spacing={3} gap={2}>
      <Typography variant="body2">
        <b>{translate('common.from')}:</b> <i>{address}</i>
      </Typography>
      <Typography variant="body2">
        <b>{translate('module.withdrawal.network')}</b> <i>{chain}</i>
      </Typography>

      <RadioGroup
        value={asset}
        onChange={(e) => {
          setAsset(e.target.value as 'SOL' | 'USDC');
          setAmount('');
        }}
      >
        {choices.map((choice) => (
          <FormControlLabel
            key={choice.id}
            value={choice.id}
            control={<Radio />}
            label={choice.name}
          />
        ))}
      </RadioGroup>

      <Stack direction="row" spacing={2} alignItems="center">
        <Stack flex={1}>
          <TextField
            label={translate('module.withdrawal.amountLabel')}
            value={amount}
            onChange={(e) => AMOUNT_INPUT.test(e.target.value) && setAmount(e.target.value)}
            inputProps={{ inputMode: 'decimal' }}
            fullWidth
          />
          {exceedsBalance && (
            <FormHelperText sx={{ color: 'error.main', ml: 1, mt: 0.5 }}>
              {translate('module.withdrawal.warningAmount')}
            </FormHelperText>
          )}
        </Stack>
        <Stack>
          <Button
            variant="outlined"
            onClick={() => currentChoice && setAmount(String(currentChoice.balance))}
            disabled={!asset}
            size="small"
          >
            {translate('actions.all')}
          </Button>
        </Stack>
      </Stack>

      <TextField
        select
        label={translate('common.to')}
        value={toAddress}
        onChange={(e) => setToAddress(e.target.value)}
        error={!!allowedError}
        helperText={
          allowedError ||
          (allowed.length === 0
            ? translate('module.financeTransfers.noAllowlist', {
                _: 'No allowlisted destination: add one under Finance > Allowlist first.',
              })
            : '')
        }
        fullWidth
      >
        {allowed.map((e) => (
          <MenuItem key={e.id} value={e.address}>
            {e.label ? `${e.label} - ` : ''}
            {e.address}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label={translate('module.moneyRequests.reason', { _: 'Reason' })}
        value={reason}
        inputProps={{ maxLength: 500 }}
        onChange={(e) => setReason(e.target.value)}
        fullWidth
      />
    </Stack>
  );
};

export default TransferRequestForm;
